import React, { useState, useEffect } from 'react';
import Header from '../src/Components/Header';
import MainHeader from '../src/Components/MainHeader';
import Footer from '../src/Components/Footer';
import VideoTestimonial from '../src/Components/VideoTestimonial';
import { NavLink, useLocation } from 'react-router-dom';
import {
    FaLaptopCode, FaBullhorn, FaPaintBrush, FaAndroid, FaReact,
    FaCertificate, FaAward,
    FaCheckCircle, FaPlus, FaMinus, FaUserGraduate,
    FaArrowRight, FaCheck, FaUser,
    FaEnvelope, FaPhone, FaBuilding, FaLock, FaGift,
    FaShieldAlt, FaChevronDown,
    FaTimes, FaUndo, FaChartBar, FaDatabase,
    FaBrain, FaRobot,
    FaTools, FaFolderOpen,
    FaStar
} from 'react-icons/fa';
import './InternshipPage.css';
import SEOHelmet from '../src/Components/SEOHelmet';

const FaqItem = ({ question, answer, index }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="faq-item" data-aos-delay={index * 100}>
            <button className="faq-question" onClick={() => setIsOpen(!isOpen)}>
                <span>{question}</span>
                <span className="faq-icon">{isOpen ? <FaMinus /> : <FaPlus />}</span>
            </button>
            <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
                <p>{answer}</p>
            </div>
        </div>
    );
};

const ScrollToTopLink = ({ to, children, ...props }) => (
    <NavLink to={to} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} {...props}>
        {children}
    </NavLink>
);


/* ─── #8 Curriculum data for each track ─── */
const trackCurriculum = [
    {
        icon: <FaChartBar />, title: "Data Analytics",
        weeks: [
            "Phase 1: Python for data analysis — NumPy, Pandas, data cleaning & wrangling",
            "Phase 2: Data visualization with Matplotlib, Seaborn & Power BI / Tableau basics",
            "Phase 3: SQL for analytics, exploratory data analysis (EDA), statistical reasoning",
            "Phase 4: Live project — analyze an industry dataset and present actionable business insights"
        ],
        outcome: "Build an end-to-end data analytics portfolio project with dashboards & reports"
    },
    {
        icon: <FaBrain />, title: "AI/ML Engineering",
        weeks: [
            "Phase 1: Python for ML — NumPy, Pandas, Scikit-learn, data preprocessing & EDA",
            "Phase 2: Supervised learning — regression, classification, model evaluation & tuning",
            "Phase 3: Deep learning — neural networks, CNNs, transfer learning with TensorFlow/PyTorch",
            "Phase 4: Live project — build & deploy an ML model as a REST API (FastAPI + Docker)"
        ],
        outcome: "Deploy a working ML model with an API endpoint and a detailed technical write-up"
    },
    {
        icon: <FaDatabase />, title: "Data Science",
        weeks: [
            "Phase 1: Python for Data Science — NumPy, Pandas, data wrangling, cleaning & transformation",
            "Phase 2: Statistics & probability, hypothesis testing, A/B testing, and feature engineering",
            "Phase 3: Machine learning for Data Science — regression, classification, clustering, model evaluation with Scikit-learn",
            "Phase 4: Capstone project — end-to-end data science pipeline: collect, clean, model, visualize & present insights"
        ],
        outcome: "Deliver a complete Data Science capstone with EDA, ML models, visualizations & a presentation-ready report"
    },
    {
        icon: <FaRobot />, title: "AI & LLM Development",
        weeks: [
            "Phase 1: Prompt engineering, OpenAI/Gemini APIs, structured outputs & function calling",
            "Phase 2: RAG — vector databases (Pinecone/ChromaDB), embedding models, LangChain & AI agents",
            "Phase 3: Full-stack AI app — React/Next.js frontend, AI-powered chat UIs, semantic search & content pipelines",
            "Phase 4: Live project — build & deploy a production RAG-powered AI SaaS application"
        ],
        outcome: "Ship a deployed AI-powered web app with RAG, LLM integration & a polished frontend"
    },
    {
        icon: <FaReact />, title: "MERN Stack Development",
        weeks: [
            "Phase 1: HTML5, CSS3, responsive design, JavaScript ES6+ fundamentals",
            "Phase 2: React — component architecture, hooks, state management, routing",
            "Phase 3: Node.js & Express — REST API design, MongoDB, authentication with JWT",
            "Phase 4: Live project — build & deploy a full-stack production app from scratch"
        ],
        outcome: "Build & deploy 2+ full-stack web applications with source code on GitHub"
    },
    {
        icon: <FaLaptopCode />, title: "PHP Stack Development",
        weeks: [
            "Phase 1: HTML, CSS, JavaScript, jQuery & AJAX fundamentals",
            "Phase 2: Core PHP, MySQL/SQL — CRUD operations, form handling, sessions",
            "Phase 3: Laravel framework — MVC architecture, Eloquent ORM, Blade templating, REST APIs",
            "Phase 4: Live project — build a complete Laravel web application with admin panel & deployment"
        ],
        outcome: "Deploy a full-stack Laravel application with database, authentication & admin dashboard"
    },
    {
        icon: <FaAndroid />, title: "Android Development",
        weeks: [
            "Phase 1: Kotlin fundamentals, Android Studio setup, UI with XML & Compose",
            "Phase 2: Activities, Fragments, Navigation, RecyclerView patterns",
            "Phase 3: Room DB, Retrofit for APIs, MVVM architecture, Firebase integration",
            "Phase 4: Live project — publish an app on Google Play Store"
        ],
        outcome: "Launch a production Android app on the Play Store with 100+ installs"
    },
    {
        icon: <FaBullhorn />, title: "Digital Marketing",
        weeks: [
            "Phase 1: SEO fundamentals, keyword research, on-page & technical SEO",
            "Phase 2: Social media strategy, content calendars, Meta & Google Ads basics",
            "Phase 3: Google Analytics, campaign optimization, email marketing funnels",
            "Phase 4: Live project — run & optimize a simulated professional ad campaign"
        ],
        outcome: "Manage a live marketing campaign with measurable ROI metrics"
    },
    {
        icon: <FaPaintBrush />, title: "Graphic Design",
        weeks: [
            "Phase 1: Design principles, color theory, typography, Figma & Adobe Suite",
            "Phase 2: Brand identity — logos, style guides, business cards, social kits",
            "Phase 3: UI/UX design, wireframing, prototyping, design systems",
            "Phase 4: Live project — create a full brand identity portfolio piece"
        ],
        outcome: "Deliver a complete brand identity package for your professional portfolio"
    },
];

/* ─── #7 Comparison table data ─── */
const comparisonFeatures = [
    { feature: "Live Production-Level Projects", octalbees: true, free: false, others: false },
    { feature: "1-on-1 Industry Mentorship", octalbees: true, free: false, others: "Varies" },
    { feature: "Online Verifiable Certificate", octalbees: true, free: false, others: false },
    { feature: "Performance-Based Stipend", octalbees: "Up to ₹20K*", free: false, others: false },
    { feature: "Flexible Part-Time Schedule", octalbees: true, free: true, others: "Varies" },
    { feature: "Structured Phase-by-Phase Curriculum", octalbees: true, free: false, others: true },
    { feature: "Price", octalbees: "₹999–₹2,299", free: "Free", others: "₹5,000–₹15,000" },
];


/* ─── #10 Expanded FAQ list with objection-handling questions ─── */
const faqs = [
    { q: "Is Octalbees a legitimate company?", a: "Absolutely. Octalbees is a registered MSME company (Udyam Registration) operating since 2024. We provide professional web development, app development, and digital marketing services. You can verify our registration and our certificates anytime on our verification portal." },
    { q: "What if I'm not satisfied? Can I get a refund?", a: "We offer a 7-day money-back guarantee before the program officially starts. If you're not satisfied within the first 7 days of registration and the internship has not yet commenced, simply reach out to us for a 100% refund — no questions asked. However, once the internship has officially commenced (i.e., you've been onboarded, assigned a mentor, or attended any session), no refund will be issued. You can view our full refund policy on our website." },
    { q: "I'm a complete beginner — can I still join?", a: "Yes! Our mentorship program is designed to guide you from foundational concepts to advanced practical implementation. Basic knowledge of your chosen domain is a plus, but not required. Your mentor will create a personalized learning path for you." },
    { q: "How is this different from free YouTube tutorials?", a: "Free tutorials teach you concepts — we give you hands-on industry experience. You'll work on advanced live projects, receive 1-on-1 feedback from mentors, build a portfolio with production-level work, and earn a verifiable certificate. This is hands-on training, not passive watching." },
    { q: "What is the daily time commitment?", a: "This is a flexible, part-time internship designed to fit alongside your college schedule. We recommend a commitment of about 16-20 hours per week. You choose when and where you work." },
    { q: "What's a sample week look like?", a: "Monday-Wednesday: Work on your assigned project tasks with guidance from your mentor. Thursday: Weekly 1-on-1 mentorship session (30-45 mins). Friday: Code/design review and feedback session. Weekends: Optional self-study and community interaction. All schedules are flexible and can be adjusted." },
    { q: "What are the criteria for receiving a stipend?", a: "Top-performing interns can earn a stipend of up to ₹20,000, based on the quality and consistency of their work. This is strictly performance-based and not guaranteed. Only those who consistently deliver quality work, show proactiveness, and contribute meaningfully to live production projects are eligible. Eligibility is evaluated after the first month, and only a select few interns receive it. If you put in the effort and produce measurable results, you stand a strong chance." },
    { q: "What happens after I pay the registration fee?", a: "After your payment is confirmed, you will receive a welcome email within 24 hours with your onboarding schedule, mentor assignment, and access to our community channels." }
];

/* ─── #8 TrackCard component with expandable curriculum ─── */
const TrackCard = ({ track, index }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    return (
        <div className="col-lg-6 col-md-6" data-aos-delay={index * 100}>
            <div className={`track-card-new ${isExpanded ? 'expanded' : ''}`} onClick={() => setIsExpanded(!isExpanded)}>
                <div className="track-card-header">
                    <div className="track-icon-new">{track.icon}</div>
                    <p className="track-text-new">{track.title}</p>
                    <FaChevronDown className={`track-expand-icon ${isExpanded ? 'rotated' : ''}`} />
                </div>
                <div className={`track-curriculum ${isExpanded ? 'open' : ''}`}>
                    <ul className="curriculum-list">
                        {track.weeks.map((week, i) => (
                            <li key={i}><FaCheck className="curriculum-check" /> {week}</li>
                        ))}
                    </ul>
                    <div className="curriculum-outcome">
                        <strong>🎯 Outcome:</strong> {track.outcome}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper to detect ambassador referral codes (email or 10-digit phone)
const isReferralCode = (code) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;
    return emailRegex.test(code) || phoneRegex.test(code);
};

const InternshipPage = () => {
    const location = useLocation();
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', college: '', track: 'MERN Stack Development', duration: '1 Month', referralCode: '' });
    const [paymentStatus, setPaymentStatus] = useState('idle');
    const [paymentId, setPaymentId] = useState('');
    /* ─── #11 Two-step form state ─── */
    const [formStep, setFormStep] = useState(1);
    const [showReferral, setShowReferral] = useState(false);
    const [showFloatingCTA, setShowFloatingCTA] = useState(false);
    const [couponState, setCouponState] = useState({ discount: 0, message: '', type: '' });
    const [couponLoading, setCouponLoading] = useState(false);

    const applyCoupon = async (codeToApply = formData.referralCode) => {
        const code = codeToApply.trim();

        if (!code) {
            setCouponState({ discount: 0, message: '', type: '' });
            return;
        }

        // Ambassador referral codes (email/phone) — no server validation needed
        if (isReferralCode(code)) {
            setCouponState({ discount: 0, message: 'Referral code noted!', type: 'info' });
            return;
        }

        // Validate coupon code against server (Google Sheet)
        setCouponLoading(true);
        try {
            const response = await fetch('/.netlify/functions/validateCoupon', {
                method: 'POST',
                body: JSON.stringify({ code }),
            });
            const data = await response.json();
            setCouponState({
                discount: data.discount || 0,
                message: data.message || '',
                type: data.valid ? 'success' : 'error'
            });
        } catch (err) {
            setCouponState({ discount: 0, message: 'Could not validate coupon. Try again.', type: 'error' });
        } finally {
            setCouponLoading(false);
        }
    };

    let basePrice = 2299;
    let originalPrice = 3500;
    if (formData.duration === '1 Month') {
        basePrice = 999;
        originalPrice = 1500;
    } else if (formData.duration === '2 Months') {
        basePrice = 1699;
        originalPrice = 2500;
    } else if (formData.duration === '3 Months') {
        basePrice = 2299;
        originalPrice = 3500;
    }

    const finalPrice = Math.round(basePrice * (1 - couponState.discount / 100));

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 400) {
                setShowFloatingCTA(true);
            } else {
                setShowFloatingCTA(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const refCode = params.get('ref');
        if (refCode) {
            setFormData(prev => ({ ...prev, referralCode: refCode }));
            setShowReferral(true);
            applyCoupon(refCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    /* ─── #11 Step 1 validation ─── */
    const handleStepOne = (e) => {
        e.preventDefault();
        if (formData.name && formData.email && formData.track) {
            setFormStep(2);
        }
    };

    const loadScript = (src) => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = src;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const displayRazorpay = async (event) => {
        event.preventDefault();
        const res = await loadScript('https://checkout.razorpay.com/v1/checkout.js');

        if (!res) {
            alert('Razorpay SDK failed to load. Are you online?');
            return;
        }

        try {
            // 1. Create order on the Netlify backend
            const orderResponse = await fetch('/.netlify/functions/createOrder', {
                method: 'POST',
                body: JSON.stringify(formData),
            });

            const orderData = await orderResponse.json();

            if (!orderResponse.ok) {
                alert('Server error creating order. Please try again.');
                return;
            }

            // 2. Initialize Razorpay Checkout with the order_id
            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: finalPrice * 100,
                currency: 'INR',
                order_id: orderData.order_id, // Pass the order ID
                name: 'Octalbees Internship Program',
                description: `${formData.duration} — ${formData.track}`,
                image: '/Images/logo-crop.jpg',
                handler: function (response) {
                    setPaymentId(response.razorpay_payment_id);
                    setPaymentStatus('success');

                    // Trigger welcome email with onboarding link
                    fetch('/.netlify/functions/sendInternshipWelcomeEmail', {
                        method: 'POST',
                        body: JSON.stringify({
                            name: formData.name,
                            email: formData.email,
                            track: formData.track,
                            duration: formData.duration,
                            paymentId: response.razorpay_payment_id
                        })
                    }).catch(err => console.error('Error sending welcome email:', err));

                    setFormData({ name: '', email: '', phone: '', college: '', track: 'Data Analytics', duration: '1 Month', referralCode: '' });
                    setFormStep(1);
                },
                prefill: { name: formData.name, email: formData.email, contact: formData.phone },
                notes: { name: formData.name, college: formData.college, track: formData.track, duration: formData.duration, referral: formData.referralCode || 'None' },
                theme: { color: '#a04ff4' },
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
        } catch (error) {
            console.error("Error creating order:", error);
            alert('Something went wrong! Please try again.');
        }
    };

    return (
        <div style={{ backgroundColor: '#0c001f', minHeight: '100vh', width: '100%' }}>
            {/* Payment Success Modal — placed at top level so position:fixed works */}
            {paymentStatus === 'success' && (
                <div className="payment-success-modal-overlay">
                    <div className="payment-success-modal">
                        <FaCheckCircle className="success-icon" />
                        <h3 className="success-title">Registration Successful!</h3>
                        <p className="success-text">Welcome aboard! Your payment has been confirmed.</p>
                        <div className="success-payment-id">Payment ID: {paymentId}</div>
                        <div className="success-48hr">
                            ⏳ You'll receive all onboarding details within <strong>48 hours</strong> on your registered email.
                        </div>

                        <div className="post-payment-referral">
                            <h4>🎉 Know someone who'd love this?</h4>
                            <p>Share & earn <strong style={{ color: '#a04ff4' }}>₹250 reward</strong> per friend!</p>
                            <ScrollToTopLink to="/ambassador" className="btn-ref-post-payment">
                                Get Referral Link <FaArrowRight />
                            </ScrollToTopLink>
                        </div>

                        <button onClick={() => setPaymentStatus('idle')} className="btn-register-again">Close</button>
                    </div>
                </div>
            )}
            <Header />
            <MainHeader />
            <div className="internship-page">
                <SEOHelmet
                    title="Internship Program – Build Industrial Skills"
                    description="Join Octalbees' hands-on internship program. Get industrial project experience, expert 1-on-1 mentorship, verified certificates, and stipend opportunities."
                    path="/internship"
                    jsonLd={{
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": faqs.map(faq => ({
                            "@type": "Question",
                            "name": faq.q,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": faq.a
                            }
                        }))
                    }}
                />

                {/* ─── Fix #9: Reframed Hero — Lead with the Problem ─── */}
                <section id="hero" className="internship-hero-new">
                    <div className="container text-center">
                        <div className="hero-content">
                            {/* Announcement Strip */}
                            <div className="hero-announcement-strip">
                                <span className="announcement-dot"></span>
                                1 to 3-Month Internship — Applications Open
                            </div>

                            {/* H1 Headline — Problem-first */}
                            <h1 className="hero-title-new">
                                You Know the Theory.<br /><span className="highlight-text">But Can You Actually Build?</span>
                            </h1>

                            {/* Subheadline — Empathy + Solution bridge */}
                            <p className="hero-subtitle-new">
                                Most students can pass exams — but can't ship a production project. This internship (1, 2, or 3 months — you choose) gives you hands-on experience, personal mentorship, and portfolio projects that bridge the gap between classroom knowledge and industry readiness.
                            </p>

                            {/* Stat Row */}
                            <div className="hero-stat-row">
                                <span className="hero-stat-item"><strong>1–3 Months</strong> Hands-On</span>
                                <span className="hero-stat-sep">·</span>
                                <span className="hero-stat-item"><strong>1-on-1</strong> Mentorship</span>
                                <span className="hero-stat-sep">·</span>
                                <span className="hero-stat-item"><strong>200+</strong> Intern Alumni</span>
                            </div>

                            {/* Dual CTA Group */}
                            <div className="hero-cta-group">
                                <a href="#register" className="btn-hero-new" id="hero-cta-btn">
                                    Start Building Live Projects <FaArrowRight />
                                </a>
                                <a href="#tracks" className="btn-hero-secondary" id="hero-secondary-btn">
                                    Explore Tracks & Curriculum <FaChevronDown />
                                </a>
                            </div>


                            {/* Trust Grid — 2×2 mini cards */}
                            <div className="hero-trust-grid">
                                <div className="hero-trust-card">
                                    <div className="hero-trust-icon-wrap"><FaStar className="hero-trust-icon star-icon" /></div>
                                    <div className="hero-trust-text"><strong>4.9/5</strong> Rating<br/><span className="hero-trust-sub">200+ Interns</span></div>
                                </div>
                                <div className="hero-trust-card">
                                    <div className="hero-trust-icon-wrap"><FaShieldAlt className="hero-trust-icon shield-icon" /></div>
                                    <div className="hero-trust-text"><strong>Govt.</strong><br/><span className="hero-trust-sub">Registered</span></div>
                                </div>
                                <div className="hero-trust-card">
                                    <div className="hero-trust-icon-wrap"><FaUndo className="hero-trust-icon refund-icon" /></div>
                                    <div className="hero-trust-text"><strong>7-Day*</strong><br/><span className="hero-trust-sub">Refund Guarantee</span></div>
                                </div>
                                <div className="hero-trust-card">
                                    <div className="hero-trust-icon-wrap"><FaAward className="hero-trust-icon cert-icon" /></div>
                                    <div className="hero-trust-text"><strong>Verified</strong><br/><span className="hero-trust-sub">Certificate</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="hero-background-elements">
                        <div className="animated-shape shape-glow-1"></div>
                        <div className="animated-shape shape-glow-2"></div>
                    </div>
                </section>



                {/* ─── Tracks Section ─── */}
                <section id="tracks" className="internship-section">
                    <div className="container">
                        <div className="section-heading-container">
                            <h2 className="section-heading">Choose Your Track</h2>
                            <p className="section-subheading">Click any track to explore the full curriculum and what you'll ship.</p>
                        </div>
                        <div className="row g-4 justify-content-center">
                            {trackCurriculum.map((track, i) => (
                                <TrackCard key={i} track={track} index={i} />
                            ))}
                        </div>
                    </div>
                </section>

                {/* ─── Video Testimonials — Social Proof After Tracks ─── */}
                <VideoTestimonial />


                {/* ─── Comparison Table Section ─── */}
                <section className="internship-section">
                    <div className="container">
                        <div className="section-heading-container">
                            <h2 className="section-heading">Why Choose Octalbees?</h2>
                            <p className="section-subheading">See how we compare to free courses and other paid internship programs.</p>
                        </div>
                        <div className="comparison-table-wrapper">
                            <table className="comparison-table">
                                <thead>
                                    <tr>
                                        <th>Feature</th>
                                        <th className="highlight-col">Octalbees</th>
                                        <th>Free Courses</th>
                                        <th>Other Paid Programs</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comparisonFeatures.map((row, i) => (
                                        <tr key={i}>
                                            <td className="feature-name">{row.feature}</td>
                                            <td className="highlight-col">
                                                {row.octalbees === true ? <FaCheckCircle className="compare-yes" /> :
                                                    row.octalbees === false ? <FaTimes className="compare-no" /> :
                                                        <span className="compare-text highlight">{row.octalbees}</span>}
                                            </td>
                                            <td>
                                                {row.free === true ? <FaCheckCircle className="compare-yes" /> :
                                                    row.free === false ? <FaTimes className="compare-no" /> :
                                                        <span className="compare-text">{row.free}</span>}
                                            </td>
                                            <td>
                                                {row.others === true ? <FaCheckCircle className="compare-yes" /> :
                                                    row.others === false ? <FaTimes className="compare-no" /> :
                                                        <span className="compare-text">{row.others}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                {/* ─── Fix #7: Reframed — What You'll Leave With (Certificate is ONE item) ─── */}
                <section className="internship-section bg-alt">
                    <div className="container">
                        <div className="section-heading-container">
                            <h2 className="section-heading">What You'll Leave With</h2>
                            <p className="section-subheading">The certificate proves your work — but the true value is everything behind it.</p>
                        </div>
                        <div className="leave-with-grid">
                            <div className="leave-with-card leave-with-featured">
                                <div className="leave-with-icon"><FaFolderOpen /></div>
                                <h4>Portfolio Projects</h4>
                                <p>Deployed, working applications you built from scratch — with source code on GitHub. Tangible proof of what you can do, not just what you studied.</p>
                            </div>
                            <div className="leave-with-card">
                                <div className="leave-with-icon"><FaTools /></div>
                                <h4>Practical Skills</h4>
                                <p>The ability to build, debug, deploy, use Git, consume APIs, and work through development tasks — skills you can demonstrate on day one of any job.</p>
                            </div>
                            <div className="leave-with-card">
                                <div className="leave-with-icon"><FaCertificate /></div>
                                <h4>Verified Certificate</h4>
                                <p>A professional certificate with a unique ID and QR code that anyone — recruiters, colleges, employers — can verify instantly on our portal.</p>
                                <ScrollToTopLink to="/verify" className="leave-with-link">
                                    Verify a certificate <FaArrowRight />
                                </ScrollToTopLink>
                            </div>

                            <div className="leave-with-card">
                                <div className="leave-with-icon"><FaAward /></div>
                                <h4>Performance Stipend</h4>
                                <p>Top performers can earn up to <strong style={{ color: '#4ade80' }}>₹20,000</strong> based on overall performance and live project contributions. Not guaranteed — entirely merit-based.*</p>
                            </div>
                        </div>
                        <div className="leave-with-certificate-preview">
                            <img
                                src="/Images/sample_certificate.jpg"
                                alt="Sample Internship Certificate"
                                className="certificate-image-compact"
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/1000x700/0c001f/a04ff4?text=Sample+Certificate'; }}
                            />
                        </div>
                    </div>
                </section>

                {/* ─── #2 Why Trust Octalbees Section ─── */}
                <section className="internship-section">
                    <div className="container">
                        <div className="section-heading-container">
                            <h2 className="section-heading">Why Trust Octalbees?</h2>
                            <p className="section-subheading">We're not a random internet page — we're a registered IT company with a proven track record.</p>
                        </div>
                        <div className="trust-section-grid">
                            <div className="trust-card">
                                <div className="trust-card-icon"><FaShieldAlt /></div>
                                <h4>Government Registered</h4>
                                <p>Octalbees is a Government of India registered MSME company under Udyam Registration and the State Shop Act. We are a legitimate business entity.</p>
                            </div>
                            <div className="trust-card">
                                <div className="trust-card-icon"><FaUserGraduate /></div>
                                <h4>200+ Intern Alumni</h4>
                                <p>Over 200 student interns have completed our program, built production-grade portfolio projects, and earned verifiable certificates.</p>
                            </div>
                            <div className="trust-card">
                                <div className="trust-card-icon"><FaUndo /></div>
                                <h4>7-Day Refund Policy*</h4>
                                <p>Not happy? Get a full refund within 7 days, no questions asked. We're confident you'll love the program — but the safety net is there.</p>
                                <ScrollToTopLink to="/cancellation-and-refund-policy" className="trust-link" style={{ fontSize: '0.82rem', marginTop: '8px' }}>*Terms apply — Read full refund policy <FaArrowRight /></ScrollToTopLink>
                            </div>
                        </div>
                    </div>
                </section>


                <section id="register" className="internship-section bg-alt">
                    <div className="container">
                        <div className="registration-split">
                            {/* Left Panel - Premium Value Proposition */}
                            <div className="reg-left-panel">
                                <div className="reg-left-accent"></div>
                                <div className="reg-left-header">
                                    <span className="reg-label">INTERNSHIP PROGRAM</span>
                                    <h3>Bridge the Gap.<br /><span className="reg-highlight">Build What Employers Want to See.</span></h3>
                                </div>

                                <div className="reg-stats-row">
                                    <div className="reg-stat">
                                        <strong>1–3 Months</strong>
                                        <span>Hands-On Work</span>
                                    </div>
                                    <div className="reg-stat-divider"></div>
                                    <div className="reg-stat">
                                        <strong>1-on-1</strong>
                                        <span>Weekly Mentorship</span>
                                    </div>
                                    <div className="reg-stat-divider"></div>
                                    <div className="reg-stat">
                                        <strong>200+</strong>
                                        <span>Intern Alumni</span>
                                    </div>
                                </div>

                                <div className="reg-checklist">
                                    <div className="reg-check-item"><FaCheckCircle className="reg-check-icon" /> Build & deploy production projects for your portfolio</div>
                                    <div className="reg-check-item"><FaCheckCircle className="reg-check-icon" /> Personal mentor — weekly 1-on-1 sessions</div>
                                    <div className="reg-check-item"><FaCheckCircle className="reg-check-icon" /> Learn to debug, use Git, and work with APIs</div>
                                    <div className="reg-check-item"><FaCheckCircle className="reg-check-icon" /> Verified certificate with unique QR code</div>
                                    <div className="reg-check-item"><FaCheckCircle className="reg-check-icon" /> Flexible schedule — fits around college</div>
                                </div>

                                <div className="price-box-new">
                                    <div className="price-box-left">
                                        <div className="price-sub">One-Time Registration</div>
                                        <div className="price-main-new">
                                            <span className="price-current">₹{finalPrice}</span>
                                            <span className="price-original">₹{originalPrice}</span>
                                        </div>
                                    </div>
                                    <div className="price-save-badge">SAVE ₹{originalPrice - finalPrice}</div>
                                </div>

                                <div className="guarantee-badge">
                                    <FaShieldAlt className="guarantee-icon" />
                                    <div>
                                        <strong>7-Day Money-Back Guarantee*</strong>
                                        <p>Not satisfied? Full refund, no questions asked. <ScrollToTopLink to="/cancellation-and-refund-policy" style={{ color: '#a04ff4', fontSize: '0.82rem' }}>*T&C apply</ScrollToTopLink></p>
                                    </div>
                                </div>
                            </div>

                            {/* Right Panel - 2-Step Form */}
                            <div className="reg-right-panel">
                                <h4 className="reg-form-title">{formStep === 1 ? 'Start Your Application' : 'Complete Registration'}</h4>
                                {/* Step Indicator */}
                                <div className="form-steps-indicator">
                                    <div className={`step-dot ${formStep >= 1 ? 'active' : ''}`}>
                                        <span>1</span>
                                        <p>Your Info</p>
                                    </div>
                                    <div className="step-line">
                                        <div className={`step-line-fill ${formStep >= 2 ? 'filled' : ''}`}></div>
                                    </div>
                                    <div className={`step-dot ${formStep >= 2 ? 'active' : ''}`}>
                                        <span>2</span>
                                        <p>Pay & Join</p>
                                    </div>
                                </div>

                                {formStep === 1 ? (
                                    /* Step 1: Name, Email, Track */
                                    <form className="registration-form" onSubmit={handleStepOne}>
                                        <div className="row">
                                            <div className="col-12 reg-input-group">
                                                <FaUser className="reg-input-icon" />
                                                <input type="text" name="name" placeholder="Full Name" required onChange={handleInputChange} value={formData.name} />
                                            </div>
                                            <div className="col-12 reg-input-group">
                                                <FaEnvelope className="reg-input-icon" />
                                                <input type="email" name="email" placeholder="Email Address" required onChange={handleInputChange} value={formData.email} />
                                            </div>
                                            <div className="col-12 reg-input-group">
                                                <FaLaptopCode className="reg-input-icon" />
                                                <select name="track" required onChange={handleInputChange} value={formData.track}>
                                                    <option>Data Analytics</option>
                                                    <option>AI/ML Engineering</option>
                                                    <option>Data Science</option>
                                                    <option>AI & LLM Development</option>
                                                    <option>MERN Stack Development</option>
                                                    <option>PHP Stack Development</option>
                                                    <option>Android Development</option>
                                                    <option>Digital Marketing</option>
                                                    <option>Graphic Design</option>
                                                    <option>Other</option>
                                                </select>
                                            </div>
                                            <div className="col-12 reg-input-group">
                                                <FaCertificate className="reg-input-icon" />
                                                <select name="duration" required onChange={handleInputChange} value={formData.duration}>
                                                    <option>1 Month</option>
                                                    <option>2 Months</option>
                                                    <option>3 Months</option>
                                                </select>
                                            </div>
                                        </div>
                                        <button type="submit" className="btn-register">
                                            Continue to Step 2 <FaArrowRight />
                                        </button>
                                        <p style={{ textAlign: 'center', color: '#8a8a8a', fontSize: '0.85rem', marginTop: '15px' }}><FaLock style={{ marginRight: '5px' }} /> Your info is safe. We never spam.</p>
                                    </form>
                                ) : (
                                    /* Step 2: Phone, College, Referral, Payment */
                                    <form className="registration-form" onSubmit={displayRazorpay}>
                                        <div className="step2-summary">
                                            <div className="step2-avatar">{formData.name.charAt(0).toUpperCase()}</div>
                                            <div>
                                                <p>Hey <strong>{formData.name}</strong> 👋</p>
                                                <p className="step2-track"><span className="step2-track-badge">{formData.track}</span> <span className="step2-track-badge">{formData.duration}</span></p>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-12 reg-input-group">
                                                <FaPhone className="reg-input-icon" />
                                                <input type="tel" name="phone" placeholder="Phone Number (for WhatsApp group)" required onChange={handleInputChange} value={formData.phone} />
                                            </div>
                                            <div className="col-12 reg-input-group">
                                                <FaBuilding className="reg-input-icon" />
                                                <input type="text" name="college" placeholder="College/University Name" required onChange={handleInputChange} value={formData.college} />
                                            </div>
                                            {showReferral ? (
                                                <div className="col-12">
                                                    <div className="reg-input-group" style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
                                                        <div style={{ position: 'relative', flex: '1 1 auto', minWidth: 0 }}>
                                                            <FaGift className="reg-input-icon" />
                                                            <input type="text" name="referralCode" placeholder="Referral / Coupon Code" onChange={handleInputChange} value={formData.referralCode} />
                                                        </div>
                                                        <button type="button" onClick={() => applyCoupon()} disabled={couponLoading} className="btn-apply-coupon" style={{
                                                            background: 'rgba(160, 79, 244, 0.1)',
                                                            border: '1px solid rgba(160, 79, 244, 0.3)',
                                                            color: '#a04ff4',
                                                            borderRadius: '12px',
                                                            padding: '0 20px',
                                                            cursor: couponLoading ? 'wait' : 'pointer',
                                                            fontWeight: '600',
                                                            transition: 'all 0.3s',
                                                            flexShrink: 0,
                                                            whiteSpace: 'nowrap',
                                                            opacity: couponLoading ? 0.6 : 1
                                                        }}>{couponLoading ? 'Checking...' : 'Apply'}</button>
                                                    </div>
                                                    {couponState.message && (
                                                        <div style={{ color: couponState.type === 'success' ? '#4ade80' : couponState.type === 'error' ? '#f87171' : '#a04ff4', fontSize: '0.9rem', marginTop: '10px', paddingLeft: '5px', fontWeight: '500' }}>
                                                            {couponState.message}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="col-12">
                                                    <button type="button" className="referral-toggle" onClick={() => setShowReferral(true)}>
                                                        <FaGift /> Have a referral code?
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <button type="submit" className="btn-register">
                                            Pay ₹{finalPrice} & Register Now <FaArrowRight />
                                        </button>
                                        <div className="payment-trust-strip">
                                            <p><FaLock /> Secure Payment via Razorpay</p>
                                            <p><FaShieldAlt /> 7-Day Money-Back Guarantee*</p>
                                        </div>
                                        <button type="button" className="btn-back-step" onClick={() => setFormStep(1)}>
                                            ← Back to Step 1
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ─── #10 FAQ Section (Ambassador promo removed from here — #14) ─── */}
                <section className="internship-section">
                    <div className="container">
                        <div className="section-heading-container">
                            <h2 className="section-heading">Got Questions?</h2>
                            <p className="section-subheading">Everything you need to know — including the tough questions.</p>
                        </div>
                        <div className="faq-container">
                            {faqs.map((faq, i) => (
                                <FaqItem key={i} question={faq.q} answer={faq.a} index={i} />
                            ))}
                        </div>
                    </div>
                </section>

            </div>

            <a href="#register" className={`floating-cta ${showFloatingCTA ? 'visible' : ''}`}>
                Register Now !
            </a>

            <Footer />
        </div>
    );
};

export default InternshipPage;